import { useLayoutEffect, useRef } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router";

const MAX_RESTORE_MS = 2000;

const SCROLL_POSITIONS_STORAGE_KEY = "sg-protect:main-scroll-positions";

// Mirrors react-router's <ScrollRestoration> persistence
function loadScrollPositions(): Map<string, number> {
    try {
        const raw = sessionStorage.getItem(SCROLL_POSITIONS_STORAGE_KEY);

        if (raw) {
            return new Map(Object.entries(JSON.parse(raw) as Record<string, number>));
        }
    } catch {
        // Corrupt or unavailable storage: fall back to an empty map.
    }

    return new Map();
}

let scrollPositions: Map<string, number> | undefined;

function getScrollPositions() {
    return (scrollPositions ??= loadScrollPositions());
}

// Kept apart from the persisted map, so its pathnames never reach sessionStorage.
const inMemoryScrollPositions = new Map<string, number>();

interface PageScrollRestorationOptions {
    /**
     * Keeps the positions in sessionStorage across reloads. Only the shell layout does; the key is grandfathered.
     * @default true
     */
    isPersisted?: boolean;
}

export function usePageScrollRestoration<T extends HTMLElement>({
    isPersisted = true
}: PageScrollRestorationOptions = {}) {
    const ref = useRef<T>(null);
    const { pathname } = useLocation();
    const navigationType = useNavigationType();

    useLayoutEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        const positions = isPersisted ? getScrollPositions() : inMemoryScrollPositions;
        const target = navigationType === NavigationType.Pop ? (positions.get(pathname) ?? 0) : 0;

        let rafId = 0;

        if (target <= 0) {
            element.scrollTop = 0;
        } else {
            const start = performance.now();

            const tryRestore = () => {
                if (element.scrollHeight - element.clientHeight >= target) {
                    element.scrollTop = target;

                    return;
                }

                if (performance.now() - start < MAX_RESTORE_MS) {
                    rafId = requestAnimationFrame(tryRestore);
                }
            };

            tryRestore();
        }

        const handleScroll = () => {
            positions.set(pathname, element.scrollTop);
        };

        const handlePageHide = () => {
            try {
                sessionStorage.setItem(SCROLL_POSITIONS_STORAGE_KEY, JSON.stringify(Object.fromEntries(positions)));
            } catch {
                // Storage unavailable/full: skip persistence, restoration within the session still works.
            }
        };

        element.addEventListener("scroll", handleScroll, { passive: true });

        if (isPersisted) {
            window.addEventListener("pagehide", handlePageHide);
        }

        return () => {
            cancelAnimationFrame(rafId);
            element.removeEventListener("scroll", handleScroll);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [pathname, navigationType, isPersisted]);

    return ref;
}
